import React, { useState, useEffect, useCallback } from 'react';
import texasApi from '../../services/texasApi';

export interface KafkaTopic {
  topicName: string;
  description?: string;
  owner?: string;
  retentionDays?: number;
  schemaVersion?: number;
  schemaType?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface EventSchemaVersion {
  topicName: string;
  schemaVersion: number;
  schemaType?: string;
  compatMode?: string;
  definition?: unknown;
  registeredAt?: string;
}

export interface AlertRule {
  eventType: string;
  configs?: Array<{ action_type?: string; [k: string]: unknown }>;
  updatedAt?: string;
  updatedBy?: string;
}

export interface SchemaDlqEntry {
  topicName?: string;
  messageId?: string;
  errors?: string[];
  message?: unknown;
  deadLetteredAt?: string;
}

export interface ProcessingDlqEntry {
  topicName?: string;
  event?: unknown;
  error?: string;
  deadLetteredAt?: string;
}

const SCHEMA_TYPES = ['avro', 'json'];

function unwrap<T>(res: any): T | null {
  const body = res?.data;
  if (!body || typeof body !== 'object') return null;
  if ('success' in body) return body.success ? ((body.data ?? null) as T) : null;
  return ((body.data ?? body) as T) ?? null;
}

function apiError(res: any): string | null {
  const body = res?.data;
  if (body && typeof body === 'object' && body.success === false) {
    return body.message ?? body.error ?? 'Request failed';
  }
  return null;
}

export const TexasEventStreamOps: React.FC = () => {
  const [topics, setTopics] = useState<KafkaTopic[]>([]);
  const [topicsError, setTopicsError] = useState<string | null>(null);
  const [schemaTypeFilter, setSchemaTypeFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');

  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [versions, setVersions] = useState<EventSchemaVersion[]>([]);
  const [compatMode, setCompatMode] = useState<string | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  const [rules, setRules] = useState<AlertRule[]>([]);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [schemaDlq, setSchemaDlq] = useState<SchemaDlqEntry[]>([]);
  const [processingDlq, setProcessingDlq] = useState<ProcessingDlqEntry[]>([]);
  const [dlqError, setDlqError] = useState<string | null>(null);

  const loadTopics = useCallback(async () => {
    setTopicsError(null);
    try {
      const res = await texasApi.eventStream.listTopics({
        schemaType: schemaTypeFilter || undefined,
        owner: ownerFilter || undefined,
      });
      const err = apiError(res);
      if (err) { setTopicsError(err); setTopics([]); return; }
      const data = unwrap<{ topics?: KafkaTopic[] }>(res);
      setTopics(data?.topics ?? []);
    } catch (e: any) {
      setTopicsError(e?.message ?? 'Failed to load topic catalog');
      setTopics([]);
    }
  }, [schemaTypeFilter, ownerFilter]);

  const loadTopicSchemas = useCallback(async (topicName: string) => {
    if (!topicName) return;
    setSchemaError(null);
    setVersions([]);
    setCompatMode(null);
    try {
      const res = await texasApi.eventStream.listSchemas(topicName);
      const err = apiError(res);
      if (err) { setSchemaError(err); return; }
      const data = unwrap<{ versions?: EventSchemaVersion[] }>(res);
      setVersions(data?.versions ?? []);
    } catch (e: any) {
      setSchemaError(e?.message ?? `Failed to load schemas for ${topicName}`);
    }
    try {
      const res = await texasApi.eventStream.getCompatMode(topicName);
      const err = apiError(res);
      if (err) { setSchemaError((prev) => prev ?? err); return; }
      const data = unwrap<{ compatMode?: string }>(res);
      setCompatMode(data?.compatMode ?? null);
    } catch (e: any) {
      setSchemaError((prev) => prev ?? (e?.message ?? 'Failed to load compatibility mode'));
    }
  }, []);

  const loadConsumer = useCallback(async () => {
    setRulesError(null);
    setDlqError(null);
    try {
      const res = await texasApi.eventStream.listRules();
      const err = apiError(res);
      if (err) { setRulesError(err); setRules([]); }
      else {
        const data = unwrap<{ rules?: AlertRule[] }>(res);
        setRules(data?.rules ?? []);
      }
    } catch (e: any) {
      setRulesError(e?.message ?? 'Failed to load consumer rules');
      setRules([]);
    }
    try {
      const res = await texasApi.eventStream.listDlq();
      const err = apiError(res);
      if (err) { setDlqError(err); setSchemaDlq([]); setProcessingDlq([]); return; }
      const data = unwrap<{ schemaDlq?: SchemaDlqEntry[]; processingDlq?: ProcessingDlqEntry[] }>(res);
      setSchemaDlq(data?.schemaDlq ?? []);
      setProcessingDlq(data?.processingDlq ?? []);
    } catch (e: any) {
      setDlqError(e?.message ?? 'Failed to load dead-letter queues');
      setSchemaDlq([]);
      setProcessingDlq([]);
    }
  }, []);

  useEffect(() => { loadTopics(); }, [loadTopics]);
  useEffect(() => { loadConsumer(); }, [loadConsumer]);
  useEffect(() => { loadTopicSchemas(selectedTopic); }, [selectedTopic, loadTopicSchemas]);

  const dlqTotal = schemaDlq.length + processingDlq.length;
  const nonBackward = compatMode !== null && compatMode !== 'BACKWARD';

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="event-stream-ops">
      <h1 className="text-2xl font-bold text-gray-900">
        <i className="fas fa-stream mr-2" />Event Stream Operations
      </h1>

      <section className="bg-white shadow rounded-lg p-6 space-y-4" data-testid="topic-catalog">
        <h2 className="font-bold text-indigo-600">Topic Catalog (F-254)</h2>

        <div className="flex items-end space-x-2">
          <div>
            <label htmlFor="schema-type-filter" className="block text-sm font-semibold text-gray-900">Schema type</label>
            <select
              id="schema-type-filter"
              data-testid="schema-type-filter"
              className="p-2 bg-white border rounded"
              value={schemaTypeFilter}
              onChange={(e) => setSchemaTypeFilter(e.target.value)}
            >
              <option value="">All schema types</option>
              {SCHEMA_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="owner-filter" className="block text-sm font-semibold text-gray-900">Owner</label>
            <input
              id="owner-filter"
              data-testid="owner-filter"
              className="p-2 bg-white border rounded"
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              placeholder="e.g. compliance"
            />
          </div>
        </div>

        {topicsError && (
          <div role="alert" data-testid="topics-error" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
            {topicsError}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-sm font-semibold">Topic</th>
                <th className="px-4 py-3 text-sm font-semibold">Schema type</th>
                <th className="px-4 py-3 text-sm font-semibold">Schema version</th>
                <th className="px-4 py-3 text-sm font-semibold">Owner</th>
                <th className="px-4 py-3 text-sm font-semibold">Retention (days)</th>
                <th className="px-4 py-3 text-sm"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {topics.length === 0 && !topicsError && (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-sm text-gray-500" data-testid="topics-empty">
                    Catalog returned 0 topics for this filter.
                  </td>
                </tr>
              )}
              {topics.map((t) => (
                <tr key={t.topicName} data-testid={`topic-row-${t.topicName}`}>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{t.topicName}</td>
                  <td className="px-4 py-3 text-sm">{t.schemaType ?? '-'}</td>
                  <td className="px-4 py-3 text-sm">{t.schemaVersion ?? '-'}</td>
                  <td className="px-4 py-3 text-sm">{t.owner ?? '-'}</td>
                  <td className="px-4 py-3 text-sm">{t.retentionDays ?? '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      className="text-indigo-600 hover:underline"
                      data-testid={`inspect-${t.topicName}`}
                      onClick={() => setSelectedTopic(t.topicName)}
                    >
                      Inspect schemas
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white shadow rounded-lg p-6 space-y-4" data-testid="schema-registry">
        <h2 className="font-bold text-indigo-600">Schema Registry (F-255)</h2>

        {selectedTopic ? (
          <p className="text-sm text-gray-600">Selected topic: <strong>{selectedTopic}</strong></p>
        ) : (
          <p className="text-sm text-gray-600" data-testid="no-topic-selected">
            No topic selected. Choose a topic above to load its schema versions.
          </p>
        )}

        {selectedTopic && (
          <div className="p-4 bg-gray-50 rounded border" data-testid="compat-mode">
            <span className="text-sm font-semibold text-gray-900">Compatibility mode: </span>
            {compatMode ? (
              <span
                className={nonBackward
                  ? 'px-2 py-1 rounded bg-amber-100 text-amber-900 font-semibold'
                  : 'px-2 py-1 rounded bg-green-100 text-green-900 font-semibold'}
                data-testid="compat-mode-value"
              >
                {compatMode}
              </span>
            ) : (
              <span className="text-sm text-gray-500">not reported</span>
            )}
            {nonBackward && (
              <p className="text-sm text-amber-900 mt-2" data-testid="compat-warning">
                Non-BACKWARD mode: breaking-change risk is NOT BACKWARD-compatible for new readers.
                The mode above is the backend&apos;s configured verdict; this screen does not recompute it.
              </p>
            )}
          </div>
        )}

        {schemaError && (
          <div role="alert" data-testid="schema-error" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
            {schemaError}
          </div>
        )}

        {selectedTopic && versions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-sm font-semibold">Version</th>
                  <th className="px-4 py-3 text-sm font-semibold">Schema type</th>
                  <th className="px-4 py-3 text-sm font-semibold">Compat mode</th>
                  <th className="px-4 py-3 text-sm font-semibold">Registered at</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {versions.map((v) => (
                  <tr key={`${v.topicName}-${v.schemaVersion}`} data-testid={`schema-version-${v.schemaVersion}`}>
                    <td className="px-4 py-3 text-sm">v{v.schemaVersion}</td>
                    <td className="px-4 py-3 text-sm">{v.schemaType ?? '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={v.compatMode && v.compatMode !== 'BACKWARD'
                          ? 'px-2 py-1 rounded bg-amber-100 text-amber-900 font-semibold'
                          : 'px-2 py-1 rounded bg-gray-100 text-gray-900'}
                        data-testid={`version-compat-${v.schemaVersion}`}
                      >
                        {v.compatMode ?? '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{v.registeredAt ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedTopic && versions.length === 0 && !schemaError && (
          <p className="text-sm text-gray-500" data-testid="versions-empty">
            No schema versions registered for {selectedTopic}.
          </p>
        )}
      </section>

      <section className="bg-white shadow rounded-lg p-6 space-y-4" data-testid="consumer-rules">
        <h2 className="font-bold text-indigo-600">Consumer Rule Mapping (F-256)</h2>

        {rulesError && (
          <div role="alert" data-testid="rules-error" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
            {rulesError}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-sm font-semibold">Event type</th>
                <th className="px-4 py-3 text-sm font-semibold">Mapped action(s)</th>
                <th className="px-4 py-3 text-sm font-semibold">Updated at</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rules.length === 0 && !rulesError && (
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-sm text-gray-500" data-testid="rules-empty">
                    0 consumer rules registered.
                  </td>
                </tr>
              )}
              {rules.map((r) => (
                <tr key={r.eventType} data-testid={`rule-${r.eventType}`}>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{r.eventType}</td>
                  <td className="px-4 py-3 text-sm">
                    {(r.configs ?? []).length === 0
                      ? <span className="text-amber-800">no action mapped</span>
                      : (r.configs ?? []).map((c, i) => (
                        <span key={i} className="px-2 py-1 rounded bg-gray-100 text-gray-900 mr-1" data-testid={`rule-action-${r.eventType}-${i}`}>
                          {c.action_type ?? 'unknown_action'}
                        </span>
                      ))}
                  </td>
                  <td className="px-4 py-3 text-sm">{r.updatedAt ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white shadow rounded-lg p-6 space-y-4" data-testid="dlq-state">
        <h2 className="font-bold text-indigo-600">
          Dead-Letter Queues (F-256){' '}
          <span
            className={dlqTotal > 0 ? 'px-2 py-1 rounded bg-red-100 text-red-900 font-semibold' : 'px-2 py-1 rounded bg-green-100 text-green-900 font-semibold'}
            data-testid="dlq-total"
          >
            {dlqTotal} entr{dlqTotal === 1 ? 'y' : 'ies'}
          </span>
        </h2>

        {dlqError && (
          <div role="alert" data-testid="dlq-error" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
            {dlqError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded border">
            <h3 className="font-semibold text-sm text-gray-900 mb-2">
              Schema DLQ (<span data-testid="schema-dlq-count">{schemaDlq.length}</span>)
            </h3>
            {schemaDlq.length === 0 && !dlqError && (
              <p className="text-sm text-gray-500" data-testid="schema-dlq-empty">
                Schema DLQ is empty &mdash; 0 dead-lettered messages.
              </p>
            )}
            {schemaDlq.map((entry, i) => (
              <div key={entry.messageId ?? i} className="p-2 bg-white border rounded mb-2" data-testid={`schema-dlq-${entry.messageId ?? i}`}>
                <div className="text-sm font-semibold text-gray-900">{entry.topicName ?? 'unknown topic'}</div>
                <div className="text-sm text-gray-600">message {entry.messageId ?? '-'}</div>
                <div className="text-sm text-gray-600">dead-lettered {entry.deadLetteredAt ?? '-'}</div>
                <ul className="text-sm text-red-800 list-disc pl-5">
                  {(entry.errors ?? []).map((e, j) => <li key={j} data-testid={`schema-dlq-error-${entry.messageId ?? i}-${j}`}>{e}</li>)}
                </ul>
              </div>
            ))}
          </div>

          <div className="p-4 bg-gray-50 rounded border">
            <h3 className="font-semibold text-sm text-gray-900 mb-2">
              Processing DLQ (<span data-testid="processing-dlq-count">{processingDlq.length}</span>)
            </h3>
            {processingDlq.length === 0 && !dlqError && (
              <p className="text-sm text-gray-500" data-testid="processing-dlq-empty">
                Processing DLQ is empty &mdash; 0 dead-lettered messages.
              </p>
            )}
            {processingDlq.map((entry, i) => (
              <div key={`${entry.topicName ?? 'dlq'}-${i}`} className="p-2 bg-white border rounded mb-2" data-testid={`processing-dlq-${i}`}>
                <div className="text-sm font-semibold text-gray-900">{entry.topicName ?? 'unknown topic'}</div>
                <div className="text-sm text-gray-600">dead-lettered {entry.deadLetteredAt ?? '-'}</div>
                <div className="text-sm text-red-800" data-testid={`processing-dlq-error-${i}`}>{entry.error ?? 'no error recorded'}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TexasEventStreamOps;